/**
 * RESPONSIBILITY: the collapsible panel a @Group of fields is edited in.
 *
 * A layout, not a control — it renders no value of its own and dispatches its children
 * back to the registry. `<details>` rather than a hand-rolled toggle: open and closed is
 * exactly what the element means, and it keeps working with the keyboard for free.
 *
 * Registers with a tester + rank, like every other renderer here.
 */
import { rankWith, uiTypeIs, type LayoutProps, type UISchemaElement } from '@jsonforms/core';
import { withJsonFormsLayoutProps, JsonFormsDispatch } from '@jsonforms/react';

/** Intersection, not `extends`: UISchemaElement carries an index signature. */
type GroupSchema = UISchemaElement & {
  label?: string;
  elements?: UISchemaElement[];
  options?: { open?: boolean };
};

function GroupLayoutBase({ uischema, schema, path, renderers, cells, visible }: LayoutProps) {
  const group = uischema as GroupSchema;
  if (visible === false) return null;

  return (
    // Open unless the annotation asked otherwise: a panel that hides a field an editor
    // came to change is worse than a long form.
    <details className="ctl-group" open={group.options?.open !== false}>
      <summary className="ctl-group-head">{group.label}</summary>
      <div className="ctl-group-body">
        {(group.elements ?? []).map((child, i) => (
          <JsonFormsDispatch
            key={i}
            uischema={child}
            schema={schema}
            path={path}
            renderers={renderers}
            cells={cells}
          />
        ))}
      </div>
    </details>
  );
}

export const groupLayout = {
  tester: rankWith(10, uiTypeIs('Group')),
  renderer: withJsonFormsLayoutProps(GroupLayoutBase),
};
